<template>
  <div>
    <order-detail
      ref="orderDetailPage"
      :is-shipping="isShipping"
      @ship-now="onClickShipNow"
    />
    <div>
      <b-modal
        :id="SHIPMENT_SETTINGS_MODAL_ID"
        size="xl"
        modal-class="modal-primary"
        body-bg-variant="light"
        centered
        title="Shipment Settings"
        hide-footer
        @hide="cancelShipment"
      >
        <order-detail
          v-if="shipmentOrder"
          :key="shipmentOrderKey"
          :shipment-order="shipmentOrder"
          :is-shipping="isShipping"
          @cancel-create-shipment="cancelShipment"
          @create-shipment="confirmShipment"
          @open-import-export="openImportExportModal"
        />
      </b-modal>
    </div>

    <!-- Credit Limit Warning Modal -->
    <b-modal
      id="shipment_restrict_modal"
      size="lg"
      modal-class="modal-warning custom-model"
      centered
      hide-header-close
      hide-header
      hide-footer
    >
      <div>
        <div class="pb-50 border-bottom">
          <span class="custom-modal-heading">Create Shipment Warning </span>
          <feather-icon
            class="float-right close-x-icon"
            icon="XIcon"
            size="24"
            @click="$bvModal.hide('shipment_restrict_modal')"
          />
        </div>
        <p
          class="mt-1 text-center product-message"
          style="white-space: pre-line;"
        >
          {{ createShipmentWarningMessage }}
        </p>
      </div>
    </b-modal>

    <!-- Quantity Validation Error Modal -->
    <b-modal
      id="shipment_quantity_error_modal"
      size="lg"
      modal-class="modal-danger custom-model"
      centered
      hide-header-close
      hide-header
      hide-footer
    >
      <div>
        <div class="pb-1 border-bottom d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="AlertTriangleIcon"
              size="22"
              class="text-danger mr-75"
            />
            <span class="custom-modal-heading">Quantity exceeds ordered amount</span>
          </div>
          <feather-icon
            class="close-x-icon cursor-pointer"
            icon="XIcon"
            size="24"
            @click="$bvModal.hide('shipment_quantity_error_modal')"
          />
        </div>

        <!-- Summary -->
        <div class="mt-1 mb-1">
          <p class="text-muted mb-0">
            <b-badge
              variant="danger"
              class="mr-50"
            >
              {{ quantityErrors.length }}
            </b-badge>
            item(s) have quantities exceeding ordered amount.
          </p>
        </div>

        <div :class="['quantity-error-table', { 'expanded': showAllErrors }]">
          <b-table-simple
            responsive
            bordered
            small
            class="mb-0"
          >
            <b-thead head-variant="light">
              <b-tr>
                <b-th>SKU</b-th>
                <b-th class="text-center">
                  Requested
                </b-th>
                <b-th class="text-center">
                  Available order quantity
                </b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr
                v-for="(error, index) in displayedQuantityErrors"
                :key="index"
              >
                <b-td>
                  <span class="font-weight-bold">{{ error.sku }}</span>
                </b-td>
                <b-td class="text-center">
                  <b-badge variant="light-danger">
                    {{ error.requested }}
                  </b-badge>
                </b-td>
                <b-td class="text-center">
                  <b-badge variant="light-success">
                    {{ error.available }}
                  </b-badge>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </div>

        <!-- Show more/less toggle -->
        <div
          v-if="quantityErrors.length > MAX_DISPLAYED_ERRORS"
          class="text-center mt-1 py-75 bg-light rounded"
        >
          <span
            v-if="!showAllErrors"
            class="text-muted"
          >
            and <b-badge variant="secondary">{{ remainingErrorsCount }}</b-badge> more item(s)...
          </span>
          <b-button
            variant="link"
            size="sm"
            class="p-0"
            :class="{ 'ml-50': !showAllErrors }"
            @click="showAllErrors = !showAllErrors"
          >
            {{ showAllErrors ? 'Show Less' : 'Show All' }}
          </b-button>
        </div>

        <div class="mt-1 pt-1 border-top text-right">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="$bvModal.hide('shipment_quantity_error_modal')"
          >
            Close
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Shipment Import/Export Modal -->
    <b-modal
      id="shipment-import-export-modal"
      size="lg"
      modal-class="modal-primary custom-model"
      centered
      hide-header-close
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <div>
        <div class="pb-50 border-bottom">
          <span class="custom-modal-heading">Shipment Import/Export</span>
          <feather-icon
            class="float-right close-x-icon cursor-pointer"
            icon="XIcon"
            size="24"
            @click="closeImportExportModal"
          />
        </div>
        <p class="mt-1">
          Download the shipment template, fill in the quantities and upload
          <b-button
            class="ml-1"
            variant="primary"
            :disabled="isExportingShipment"
            @click="exportShipment"
          >
            <div class="d-flex align-items-center">
              <b-spinner
                v-if="isExportingShipment"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="DownloadIcon"
                class="mr-50"
                size="18"
              />
              {{ isExportingShipment ? 'Exporting...' : 'Export Shipment' }}
            </div>
          </b-button>
        </p>
        <label
          v-if="!shipmentImportFile"
          class="fileDownload elements-center"
          @click="$refs.shipmentFileInput.click()"
        >
          <feather-icon
            icon="UploadCloudIcon"
            size="50"
            color="skyblue"
          />
          <h4 class="font-weight-bolder">
            <b>Drag & Drop</b>
          </h4>
          <h6>
            or click to select files from your system
          </h6>
        </label>
        <div
          v-if="shipmentImportFile"
          class="d-flex file-droped mt-50"
        >
          <b-img
            :src="xlsxIcon"
            class="mx-1"
            alt="xlsx"
          />
          <span class="catalog-label name">Name: {{ shipmentImportFile.name }} </span>
          <span
            class="catalog-label size"
          >Size: {{ getSizeInKb(shipmentImportFile.size) }} KB</span>
          <div
            class="delete-file d-flex align-items-center justify-content-center"
            @click="removeShipmentFile"
          >
            <feather-icon
              icon="TrashIcon"
              size="24"
              fill="white"
              color="white"
            />
          </div>
        </div>
        <input
          ref="shipmentFileInput"
          type="file"
          accept=".xlsx, .xls"
          style="display: none;"
          @change="onShipmentFileChange($event)"
        >
      </div>
      <div>
        <div class="text-center mt-1">
          <b-button
            type="reset"
            class="mt-2 mr-2 px-sm-3 font-weight-bolder"
            variant="outline-info"
            @click="closeImportExportModal"
          >
            Cancel
          </b-button>
          <b-button
            type="submit"
            variant="info"
            class="mt-2 px-sm-3 font-weight-bolder"
            :disabled="isImportingShipment || !shipmentImportFile"
            @click="importShipment"
          >
            {{ isImportingShipment ? 'Importing...' : 'Import' }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  VBModal,
  BModal,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
  BBadge,
  BButton,
  BSpinner,
  BImg,
} from 'bootstrap-vue'
import { useShipmentCreation } from '@/composables/useShipmentCreation'
import OrderDetail from './OrderDetail.vue'

export default {
  name: 'Order',
  components: {
    'order-detail': OrderDetail,
    BModal,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BBadge,
    BButton,
    BSpinner,
    BImg,
  },
  directives: {
    'b-modal': VBModal,
  },
  setup() {
    const s = useShipmentCreation()
    return {
      // State
      shipmentOrder: s.shipmentOrder,
      isShipping: s.isShipping,
      totalOrderProductsCount: s.totalOrderProductsCount,
      createShipmentWarningMessage: s.createShipmentWarningMessage,
      quantityErrors: s.quantityErrors,
      showAllErrors: s.showAllErrors,
      shipmentImportFile: s.shipmentImportFile,
      isExportingShipment: s.isExportingShipment,
      isImportingShipment: s.isImportingShipment,
      isImportExportOpen: s.isImportExportOpen,
      shipmentOrderKey: s.shipmentOrderKey,
      // Computeds
      displayedQuantityErrors: s.displayedQuantityErrors,
      remainingErrorsCount: s.remainingErrorsCount,
      // Constants
      SHIPMENT_SETTINGS_MODAL_ID: s.SHIPMENT_SETTINGS_MODAL_ID,
      MAX_DISPLAYED_ERRORS: s.MAX_DISPLAYED_ERRORS,
      getSizeInKb: s.getSizeInKb,
      xlsxIcon: require('@/assets/images/icons/xlsxIcon.svg'),
      // Methods that need wrapping (prefixed)
      _onClickShipNow: s.onClickShipNow,
      _confirmShipment: s.confirmShipment,
      _openImportExportModal: s.openImportExportModal,
      _closeImportExportModal: s.closeImportExportModal,
      _removeShipmentFile: s.removeShipmentFile,
      _importShipment: s.importShipment,
      // Methods that can be used directly
      onShipmentFileChange: s.onShipmentFileChange,
      exportShipment: s.exportShipment,
    }
  },
  methods: {
    onClickShipNow(ShipmentOrderData, totalOrderProductsCount = 0) {
      this._onClickShipNow(ShipmentOrderData, totalOrderProductsCount)
      this.$bvModal.show(this.SHIPMENT_SETTINGS_MODAL_ID)
    },
    showModalIfNeeded(result) {
      if (result.showQuantityErrorModal) {
        this.$bvModal.show('shipment_quantity_error_modal')
      }
      if (result.showCreditLimitModal) {
        this.$bvModal.show('shipment_restrict_modal')
      }
    },
    handleShipmentSuccess() {
      this.cancelShipment()
      this.$refs.orderDetailPage.loadOrder()
    },
    handleShipmentResult(result) {
      if (!result) return
      this.showModalIfNeeded(result)
      if (result.success) {
        this.handleShipmentSuccess()
      }
    },
    async confirmShipment() {
      const result = await Promise.resolve(this._confirmShipment())
      this.handleShipmentResult(result)
    },
    cancelShipment() {
      if (this.isImportExportOpen) {
        return
      }
      this.shipmentOrder = null
      this.$bvModal.hide(this.SHIPMENT_SETTINGS_MODAL_ID)
    },
    openImportExportModal() {
      this._openImportExportModal()
      this.$bvModal.hide(this.SHIPMENT_SETTINGS_MODAL_ID)
      this.$nextTick(() => {
        this.$bvModal.show('shipment-import-export-modal')
      })
    },
    closeImportExportModal() {
      this._closeImportExportModal()
      this.$bvModal.hide('shipment-import-export-modal')
      if (this.shipmentOrder) {
        this.$nextTick(() => {
          this.$bvModal.show(this.SHIPMENT_SETTINGS_MODAL_ID)
        })
      }
    },
    removeShipmentFile() {
      this._removeShipmentFile(this.$refs.shipmentFileInput)
    },
    async importShipment() {
      const result = await this._importShipment()
      if (result?.success) {
        this.closeImportExportModal()
        this.removeShipmentFile()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.quantity-error-table {
  max-height: 280px;
  overflow-y: auto;
  border-radius: 4px;

  &.expanded {
    max-height: 400px;
  }

  .table {
    margin-bottom: 0;

    th {
      font-weight: 600;
      font-size: 0.85rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    td {
      vertical-align: middle;
    }
  }
}

.custom-modal-heading {
  font-size: 1.1rem;
  font-weight: 600;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
